import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { Analytics, AnalyticsBatch, AnalyticsPoint, AnalyticsRange, AnalyticsTotals } from '@ia/shared';
import { api, errorMessage } from '../api';
import { Alert, Spinner } from '../components/ui';
import { formatDate, STATUS_LABEL, usePolling } from '../util';

const RANGES: AnalyticsRange[] = ['24h', '7d', '30d', '90d'];
type Subject = 'batches' | 'policies';
const SUBJECTS: { key: Subject; label: string }[] = [
  { key: 'batches', label: 'Batches' },
  { key: 'policies', label: 'Policies' },
];

const num = new Intl.NumberFormat();
const dayFmt = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
const hourFmt = new Intl.DateTimeFormat(undefined, { hour: 'numeric' });
const dayHourFmt = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

/** Succeeded as a share of succeeded plus failed; null when there was nothing to count. */
const rateOf = (t: AnalyticsTotals) => (t.succeeded + t.failed ? t.succeeded / (t.succeeded + t.failed) : null);
const pct = (r: number) => `${(r * 100).toFixed(1)}%`;

type Delta = { text: string; dir: 'up' | 'down' | 'flat'; tone: 'good' | 'bad' | 'muted' };

function change(cur: number, prev: number, higherIsBetter: boolean): Delta {
  if (cur === prev) return { text: prev ? '0%' : '—', dir: 'flat', tone: 'muted' };
  const dir = cur > prev ? 'up' : 'down';
  return { text: prev ? `${((Math.abs(cur - prev) / prev) * 100).toFixed(1)}%` : 'new', dir, tone: (dir === 'up') === higherIsBetter ? 'good' : 'bad' };
}

function rateChange(cur: number | null, prev: number | null): Delta {
  if (cur === null || prev === null) return { text: '—', dir: 'flat', tone: 'muted' };
  const pts = (cur - prev) * 100;
  if (Math.abs(pts) < 0.05) return { text: '0.0 pts', dir: 'flat', tone: 'muted' };
  return { text: `${Math.abs(pts).toFixed(1)} pts`, dir: pts > 0 ? 'up' : 'down', tone: pts > 0 ? 'good' : 'bad' };
}

export function AnalyticsPage() {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const range: AnalyticsRange = (RANGES as string[]).includes(sp.get('range') ?? '') ? (sp.get('range') as AnalyticsRange) : '7d';
  const subject: Subject = sp.get('show') === 'policies' ? 'policies' : 'batches';
  const [data, setData] = useState<Analytics | null>(null);
  const [error, setError] = useState<string | null>(null);

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null) next.delete(k);
      else next.set(k, v);
    }
    setSp(next, { replace: true });
  };

  usePolling(
    async (signal) => {
      try {
        setData(await api.analytics(range, signal));
        setError(null);
      } catch (e) {
        if ((e as Error).name !== 'AbortError') setError(errorMessage(e));
      }
    },
    30_000,
    true,
    [range],
  );

  const shown = data?.range === range ? data : null;

  let body: ReactNode = null;
  if (shown) {
    const s = shown[subject];
    const labels = axisLabels(s.points, shown.step);
    const rate = rateOf(s.current);
    const perBatch = shown.recent.map((b) => ({ ...b, counts: subject === 'batches' ? b.items : b.policies }));
    const text =
      subject === 'batches'
        ? {
            volume: 'Batch volume',
            volumeHint: 'Batches received from the extension in this period',
            errors: 'Batch run errors',
            errorsHint: 'Batches whose import failed. Each bar is one batch received in this period: records that imported against records that failed. Click a bar to open the batch.',
            unit: 'records',
          }
        : {
            volume: 'Policy volume',
            volumeHint: 'Policies whose import outcome was recorded in this period (succeeded, failed or skipped)',
            errors: 'Policy errors',
            errorsHint: 'Policies the import could not write. Each bar is one batch received in this period: policies written against policies that failed. Click a bar to open the batch.',
            unit: 'policies',
          };
    const fourth =
      subject === 'batches'
        ? {
            label: 'Policies written',
            hint: 'Policies imported into Maximizer in this period, the outcome the batches exist for',
            values: shown.policies.points.map((p) => p.succeeded),
            cur: shown.policies.current.succeeded,
            prev: shown.policies.previous.succeeded,
          }
        : {
            label: 'New policies',
            hint: 'Policies created in Maximizer for the first time in this period; the other successes updated policies that were already there',
            values: shown.policies.points.map((p) => p.created),
            cur: shown.policies.current.created,
            prev: shown.policies.previous.created,
          };
    body = (
      <>
        <div className="metrics">
          <Card label={text.volume} hint={text.volumeHint} value={num.format(s.current.total)} delta={change(s.current.total, s.previous.total, true)} to="/batches?bucket=all">
            <Bars values={s.points.map((p) => p.total)} tone="volume" />
            <Axis labels={labels} />
          </Card>
          <Card
            label="Success rate"
            hint="Succeeded as a share of succeeded plus failed"
            value={rate === null ? '—' : pct(rate)}
            delta={rateChange(rate, rateOf(s.previous))}
            to="/batches?bucket=processed"
          >
            <Area values={s.points.map(rateOf)} />
            <Axis labels={labels} />
          </Card>
          <Card label={text.errors} hint={text.errorsHint} value={num.format(s.current.failed)} delta={change(s.current.failed, s.previous.failed, false)} to="/batches?bucket=failed">
            <Stacked bars={perBatch} unit={text.unit} step={shown.step} onPick={(id) => navigate(`/batches/${id}`)} />
          </Card>
          <Card label={fourth.label} hint={fourth.hint} value={num.format(fourth.cur)} delta={change(fourth.cur, fourth.prev, true)} to="/batches?bucket=processed">
            <Bars values={fourth.values} tone="other" />
            <Axis labels={labels} />
          </Card>
        </div>
        <p className="muted small">
          Batches count when they arrived, policies when the import recorded their outcome. Change is against the previous {range}. Refreshes every 30 s.
        </p>
      </>
    );
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Analytics</h1>
          <div className="muted small">{shown ? <Period a={shown} /> : 'Loading…'}</div>
        </div>
        <div className="analytics-controls">
          <div className="seg" role="group" aria-label="Show">
            {SUBJECTS.map((s) => (
              <button key={s.key} type="button" className="seg-btn" aria-pressed={subject === s.key} onClick={() => update({ show: s.key === 'batches' ? null : s.key })}>
                {s.label}
              </button>
            ))}
          </div>
          <div className="seg" role="group" aria-label="Period">
            {RANGES.map((r) => (
              <button key={r} type="button" className="seg-btn" aria-pressed={range === r} onClick={() => update({ range: r === '7d' ? null : r })}>
                {r}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && <Alert>{error}</Alert>}
      {!shown && !error && <Spinner />}
      {body}
    </div>
  );
}

function Period({ a }: { a: Analytics }) {
  const fmt = a.step === 'day' ? dayFmt : dayHourFmt;
  return (
    <>
      {fmt.format(new Date(a.from))} – {fmt.format(new Date(a.to))} · Data as of {formatDate(a.to)}
    </>
  );
}

/** Up to six evenly spaced indices (all of them for seven or fewer). */
function pickIndices(n: number): number[] {
  const count = n <= 7 ? n : 6;
  return [...new Set(Array.from({ length: count }, (_, k) => Math.round((k * (n - 1)) / Math.max(1, count - 1))))];
}

function axisLabels(points: AnalyticsPoint[], step: Analytics['step']): string[] {
  const fmt = step === 'hour' ? hourFmt : dayFmt;
  return pickIndices(points.length).map((i) => fmt.format(new Date(points[i]!.at)));
}

function Card({ label, hint, value, delta, to, children }: { label: string; hint: string; value: string; delta: Delta; to: string; children: ReactNode }) {
  return (
    <section className="card metric-card">
      <div className="metric-head">
        <span className="metric-label" title={hint}>
          {label}
        </span>
        <Link to={to} className="metric-link">
          View details →
        </Link>
      </div>
      <div className="metric-value">
        {value}
        <span className={`metric-delta tone-${delta.tone}`} title="Change against the previous period">
          {delta.dir === 'up' ? '▲ ' : delta.dir === 'down' ? '▼ ' : ''}
          {delta.text}
        </span>
      </div>
      {children}
    </section>
  );
}

function Axis({ labels }: { labels: string[] }) {
  return (
    <div className="chart-x" aria-hidden="true">
      {labels.map((l, i) => (
        <span key={i}>{l}</span>
      ))}
    </div>
  );
}

/* Charts: a fixed 520x150 drawing stretched to the card, as Deck's overview does. */
const W = 520;
const H = 150;
const PAD = 4;

function Grid() {
  return (
    <>
      {[0.25, 0.5, 0.75].map((f) => (
        <line key={f} className="chart-grid" x1={0} x2={W} y1={H * f} y2={H * f} />
      ))}
    </>
  );
}

function Bars({ values, tone }: { values: number[]; tone: 'volume' | 'other' }) {
  const max = Math.max(1, ...values);
  const bw = (W - PAD * 2) / values.length;
  return (
    <svg className={`chart chart-${tone}`} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
      <Grid />
      {values.map((v, i) => {
        const h = (v / max) * H * 0.95;
        return <rect key={i} x={PAD + i * bw + bw * 0.2} y={H - h} width={bw * 0.6} height={h} rx={1} />;
      })}
    </svg>
  );
}

/** Success rate line (0..1) with the area under it; gaps where a bucket had nothing to count. */
function Area({ values }: { values: (number | null)[] }) {
  const bw = (W - PAD * 2) / values.length;
  const els: ReactNode[] = [];
  let seg: [number, number][] = [];
  const flush = () => {
    if (seg.length === 1) els.push(<circle key={els.length} className="chart-dot" cx={seg[0]![0]} cy={seg[0]![1]} r={3} />);
    else if (seg.length > 1) {
      const d = seg.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join('');
      els.push(<path key={els.length} className="chart-area" d={`${d}L${seg[seg.length - 1]![0].toFixed(1)},${H}L${seg[0]![0].toFixed(1)},${H}Z`} />);
      els.push(<path key={els.length} className="chart-line" d={d} />);
    }
    seg = [];
  };
  values.forEach((v, i) => (v === null ? flush() : seg.push([PAD + i * bw + bw / 2, H - v * H * 0.95])));
  flush();
  return (
    <svg className="chart chart-rate" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
      <Grid />
      {els}
    </svg>
  );
}

/** One stacked bar per batch, oldest first: what imported under what failed; dates of the batches on the axis. */
function Stacked({
  bars, unit, step, onPick,
}: {
  bars: (AnalyticsBatch & { counts: AnalyticsTotals })[]; unit: string; step: Analytics['step']; onPick: (id: string) => void;
}) {
  const slots = Math.max(bars.length, 10);
  const bw = (W - PAD * 2) / slots;
  const max = Math.max(1, ...bars.map((b) => b.counts.succeeded + b.counts.failed));
  const fmt = step === 'hour' ? hourFmt : dayFmt;
  return (
    <>
      <svg className="chart chart-stacked" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
        <Grid />
        {bars.map((b, i) => {
          const ok = (b.counts.succeeded / max) * H * 0.95;
          const ko = (b.counts.failed / max) * H * 0.95;
          const x = PAD + i * bw + bw * 0.2;
          return (
            <g key={b.id} className="chart-batch" onClick={() => onPick(b.id)}>
              <title>{`${dayHourFmt.format(new Date(b.at))} · ${STATUS_LABEL[b.status] ?? b.status} · ${b.counts.succeeded} ok, ${b.counts.failed} failed ${unit}`}</title>
              <rect className="chart-ok" x={x} y={H - ok} width={bw * 0.6} height={ok} rx={1} />
              <rect className="chart-ko" x={x} y={H - ok - ko} width={bw * 0.6} height={ko} rx={1} />
            </g>
          );
        })}
      </svg>
      <div className="chart-x chart-x-abs" aria-hidden="true">
        {pickIndices(bars.length).map((i) => (
          <span key={i} style={{ left: `${((PAD + i * bw + bw / 2) / W) * 100}%` }}>
            {fmt.format(new Date(bars[i]!.at))}
          </span>
        ))}
        {!bars.length && <span className="chart-x-empty">No batches in this period</span>}
      </div>
    </>
  );
}
